import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Star, Shield, Lightbulb, Monitor, Gift, Send, Settings, Award, Check, X, Clock, Zap, Medal, Crown, Flame, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const CATEGORIES = [
  { value: 'produzione', label: 'Produzione', color: 'bg-blue-500' },
  { value: 'gestionale', label: 'Utilizzo Gestionale', color: 'bg-purple-500' },
  { value: 'sicurezza', label: 'Sicurezza', color: 'bg-green-500' },
  { value: 'comportamento', label: 'Comportamento', color: 'bg-amber-500' },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

const REWARD_TYPES = [
  { value: 'buono_carburante', label: 'Buono Carburante' },
  { value: 'buono_acquisto', label: 'Buono Acquisto' },
  { value: 'buono_pasto', label: 'Buono Pasto' },
];

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  reviewed: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  delivered: 'bg-emerald-100 text-emerald-800',
};
const STATUS_LABELS = { pending: 'In Attesa', reviewed: 'Revisionata', approved: 'Approvato', rejected: 'Rifiutato', delivered: 'Consegnato' };

const BADGE_ICONS = { trophy: Trophy, shield: Shield, lightbulb: Lightbulb, monitor: Monitor, star: Star };
const BADGE_COLORS = { gold: 'text-yellow-500', silver: 'text-slate-400', bronze: 'text-amber-700' };

function BadgeIcon({ badge }) {
  const Icon = BADGE_ICONS[badge.icon] || Star;
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-100" title={badge.name}>
      <Icon size={14} className={BADGE_COLORS[badge.level] || 'text-slate-500'} />
      <span className="text-xs font-medium">{badge.name}</span>
    </div>
  );
}

// ─── LEADERBOARD TAB ───
function LeaderboardTab({ year, month }) {
  const [data, setData] = useState(null);
  const [bestWorker, setBestWorker] = useState(null);
  const [viewMonth, setViewMonth] = useState(month);

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (viewMonth) params.month = viewMonth;
      const [lb, bw] = await Promise.all([
        api.get('/regards/leaderboard', { params }),
        api.get('/regards/best-worker', { params: { year, month: viewMonth || month } }),
      ]);
      setData(lb.data);
      setBestWorker(bw.data);
    } catch {}
  }, [year, viewMonth, month]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-center py-8 text-slate-400">Caricamento...</div>;

  const podium = data.leaderboard.slice(0, 3);
  const podiumOrder = podium.length === 3 ? [podium[1], podium[0], podium[2]] : podium;
  const podiumHeights = ['h-28', 'h-40', 'h-20'];
  const podiumColors = ['bg-slate-300', 'bg-yellow-400', 'bg-amber-600'];
  const podiumTextColors = ['text-slate-600', 'text-yellow-700', 'text-amber-800'];
  const crownIcons = [Medal, Crown, Award];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium text-slate-500">Periodo:</span>
        <Select value={viewMonth?.toString() || 'all'} onValueChange={v => setViewMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="leaderboard-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutto l'anno</SelectItem>
            {Array.from({ length: 12 }, (_, i) => (
              <SelectItem key={i + 1} value={(i + 1).toString()}>
                {new Date(2026, i).toLocaleString('it-IT', { month: 'long' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {podium.length > 0 && (
        <div className="flex items-end justify-center gap-2 pt-4" data-testid="podium">
          {podiumOrder.map((p, idx) => {
            const CrownIcon = crownIcons[idx] || Award;
            const realIdx = podium.length === 3 ? [1, 0, 2][idx] : idx;
            return (
              <div key={p.employee_id} className="flex flex-col items-center" data-testid={`podium-position-${p.position}`}>
                <CrownIcon size={realIdx === 0 ? 32 : 24} className={podiumTextColors[idx] || 'text-slate-500'} />
                <span className="text-sm font-bold mt-1">{p.employee_name}</span>
                <span className="text-xs text-slate-500">{p.total_points} pt</span>
                <div className={`${podiumHeights[idx]} w-24 ${podiumColors[idx]} rounded-t-lg mt-2 flex items-center justify-center`}>
                  <span className="text-2xl font-black text-white">{p.position}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Classifica Completa</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leaderboard-table">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2 px-2">#</th>
                  <th className="py-2 px-2">Dipendente</th>
                  <th className="py-2 px-2 text-center">Punti</th>
                  <th className="py-2 px-2 text-center hidden sm:table-cell">Produz.</th>
                  <th className="py-2 px-2 text-center hidden sm:table-cell">Gestionale</th>
                  <th className="py-2 px-2 text-center hidden sm:table-cell">Sicurezza</th>
                  <th className="py-2 px-2 text-center hidden sm:table-cell">Comport.</th>
                  <th className="py-2 px-2">Badge</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map(row => (
                  <tr key={row.employee_id} className={`border-b hover:bg-slate-50 ${row.position <= 3 ? 'bg-yellow-50/50' : ''}`}>
                    <td className="py-2 px-2 font-bold">{row.position <= 3 ? <Flame size={16} className={row.position === 1 ? 'text-yellow-500' : row.position === 2 ? 'text-slate-400' : 'text-amber-700'} /> : row.position}</td>
                    <td className="py-2 px-2 font-medium">{row.employee_name}</td>
                    <td className="py-2 px-2 text-center font-bold">{row.total_points}</td>
                    <td className="py-2 px-2 text-center hidden sm:table-cell">{row.categories.produzione}</td>
                    <td className="py-2 px-2 text-center hidden sm:table-cell">{row.categories.gestionale}</td>
                    <td className="py-2 px-2 text-center hidden sm:table-cell">{row.categories.sicurezza}</td>
                    <td className="py-2 px-2 text-center hidden sm:table-cell">{row.categories.comportamento}</td>
                    <td className="py-2 px-2"><div className="flex gap-1 flex-wrap">{row.badges.map((b, i) => <BadgeIcon key={i} badge={b} />)}</div></td>
                  </tr>
                ))}
                {data.leaderboard.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nessun dato disponibile</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {bestWorker && bestWorker.winners && bestWorker.winners.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy size={18} className="text-yellow-500" /> Best Worker del Mese</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {bestWorker.winners.map(w => (
                <div key={w.employee_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${w.position === 1 ? 'bg-yellow-100 text-yellow-700' : w.position === 2 ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>
                    <span className="text-lg font-black">{w.position}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{w.employee_name}</p>
                    <p className="text-xs text-slate-500">{w.points} pt &middot; {w.prize_euro}€</p>
                  </div>
                  {bestWorker.validated && <Check size={16} className="ml-auto text-green-500" />}
                </div>
              ))}
            </div>
            {!bestWorker.validated && <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><Clock size={12} /> In attesa di validazione Admin</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── POINTS TAB ───
function PointsTab({ year, role, employees }) {
  const [transactions, setTransactions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ employee_id: '', category: '', point_type: 'standard', points: '', description: '' });
  const [filterCat, setFilterCat] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (filterCat !== 'all') params.category = filterCat;
      if (filterType !== 'all') params.point_type = filterType;
      const res = await api.get('/regards/transactions', { params });
      setTransactions(res.data);
    } catch {}
  }, [year, filterCat, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      await api.post('/regards/transactions', { ...form, points: parseInt(form.points) });
      setShowAdd(false);
      setForm({ employee_id: '', category: '', point_type: 'standard', points: '', description: '' });
      load();
    } catch {}
  };

  const handleApprove = async (id, action) => {
    try {
      await api.put(`/regards/transactions/${id}/approve`, { action });
      load();
    } catch {}
  };

  const canAssign = ['admin', 'capo_cantiere', 'qhse'].includes(role);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-40" data-testid="points-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="points-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="bonus_speciale">Bonus Speciale</SelectItem>
            <SelectItem value="malus_speciale">Malus Speciale</SelectItem>
          </SelectContent>
        </Select>
        {canAssign && (
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button size="sm" data-testid="assign-points-btn"><Zap size={14} className="mr-1" /> Assegna Punti</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Assegna Punti REGARDS</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <Select value={form.employee_id} onValueChange={v => setForm(p => ({ ...p, employee_id: v }))}>
                  <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="assign-category-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
                {role === 'admin' && (
                  <Select value={form.point_type} onValueChange={v => setForm(p => ({ ...p, point_type: v }))}>
                    <SelectTrigger data-testid="assign-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="bonus_speciale">Bonus Speciale</SelectItem>
                      <SelectItem value="malus_speciale">Malus Speciale</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Input type="number" placeholder="Punti" value={form.points} onChange={e => setForm(p => ({ ...p, points: e.target.value }))} data-testid="assign-points-input" />
                <Input placeholder="Motivazione (obbligatoria)" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="assign-description-input" />
                <Button className="w-full" onClick={handleSubmit} disabled={!form.employee_id || !form.category || !form.points || !form.description} data-testid="assign-submit-btn">Conferma Assegnazione</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2 px-2">Data</th>
                  <th className="py-2 px-2">Dipendente</th>
                  <th className="py-2 px-2">Categoria</th>
                  <th className="py-2 px-2">Tipo</th>
                  <th className="py-2 px-2 text-center">Punti</th>
                  <th className="py-2 px-2">Motivazione</th>
                  <th className="py-2 px-2">Stato</th>
                  {role === 'admin' && <th className="py-2 px-2">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-2 text-xs">{new Date(t.created_at).toLocaleDateString('it-IT')}</td>
                    <td className="py-2 px-2 font-medium">{t.employee_name}</td>
                    <td className="py-2 px-2"><span className={`inline-block w-2 h-2 rounded-full mr-1 ${CAT_MAP[t.category]?.color || 'bg-slate-300'}`} />{CAT_MAP[t.category]?.label || t.category}</td>
                    <td className="py-2 px-2"><Badge variant="outline" className="text-xs">{t.point_type === 'standard' ? 'STD' : t.point_type === 'bonus_speciale' ? 'BONUS' : 'MALUS'}</Badge></td>
                    <td className={`py-2 px-2 text-center font-bold ${t.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>{t.points > 0 ? '+' : ''}{t.points}</td>
                    <td className="py-2 px-2 text-xs max-w-[200px] truncate">{t.description}</td>
                    <td className="py-2 px-2"><Badge className={`text-xs ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</Badge></td>
                    {role === 'admin' && (
                      <td className="py-2 px-2">
                        {t.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleApprove(t.id, 'approve')} data-testid={`approve-txn-${t.id}`}><Check size={14} /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleApprove(t.id, 'reject')} data-testid={`reject-txn-${t.id}`}><X size={14} /></Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {transactions.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nessuna transazione</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── REQUESTS TAB ───
function RequestsTab({ year, role, employees }) {
  const [requests, setRequests] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ target_employee_id: '', category: '', points_requested: '', description: '', anonymous: false, is_special: false });

  const load = useCallback(async () => {
    try {
      const res = await api.get('/regards/requests');
      setRequests(res.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      await api.post('/regards/requests', { ...form, points_requested: parseInt(form.points_requested) });
      setShowAdd(false);
      setForm({ target_employee_id: '', category: '', points_requested: '', description: '', anonymous: false, is_special: false });
      load();
    } catch {}
  };

  const handleReview = async (id, action) => {
    try {
      await api.put(`/regards/requests/${id}/review`, { action });
      load();
    } catch {}
  };

  const handleApprove = async (id, action) => {
    try {
      await api.put(`/regards/requests/${id}/approve`, { action });
      load();
    } catch {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild><Button size="sm" data-testid="create-request-btn"><Send size={14} className="mr-1" /> Nuova Richiesta</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Richiesta Punti</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <Select value={form.target_employee_id} onValueChange={v => setForm(p => ({ ...p, target_employee_id: v }))}>
                <SelectTrigger data-testid="request-employee-select"><SelectValue placeholder="Per chi?" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="request-category-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Punti richiesti" value={form.points_requested} onChange={e => setForm(p => ({ ...p, points_requested: e.target.value }))} data-testid="request-points-input" />
              <Input placeholder="Motivazione" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="request-description-input" />
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.anonymous} onChange={e => setForm(p => ({ ...p, anonymous: e.target.checked }))} className="rounded" data-testid="request-anonymous-check" />
                  Anonima
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_special} onChange={e => setForm(p => ({ ...p, is_special: e.target.checked }))} className="rounded" data-testid="request-special-check" />
                  Bonus Speciale
                </label>
              </div>
              <Button className="w-full" onClick={handleSubmit} disabled={!form.target_employee_id || !form.category || !form.points_requested || !form.description} data-testid="request-submit-btn">Invia Richiesta</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="requests-table">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2 px-2">Data</th>
                  <th className="py-2 px-2">Richiedente</th>
                  <th className="py-2 px-2">Per</th>
                  <th className="py-2 px-2">Categoria</th>
                  <th className="py-2 px-2 text-center">Punti</th>
                  <th className="py-2 px-2">Motivazione</th>
                  <th className="py-2 px-2">Stato</th>
                  <th className="py-2 px-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-2 text-xs">{new Date(r.created_at).toLocaleDateString('it-IT')}</td>
                    <td className="py-2 px-2">{r.requester_name}</td>
                    <td className="py-2 px-2 font-medium">{r.target_name}</td>
                    <td className="py-2 px-2">{CAT_MAP[r.category]?.label || r.category}</td>
                    <td className="py-2 px-2 text-center font-bold text-green-600">+{r.points_requested}</td>
                    <td className="py-2 px-2 text-xs max-w-[200px] truncate">{r.description}</td>
                    <td className="py-2 px-2"><Badge className={`text-xs ${STATUS_COLORS[r.status]}`}>{STATUS_LABELS[r.status]}</Badge></td>
                    <td className="py-2 px-2">
                      {r.status === 'pending' && ['admin', 'capo_cantiere', 'qhse'].includes(role) && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => role === 'admin' ? handleApprove(r.id, 'approve') : handleReview(r.id, 'approve')} data-testid={`approve-req-${r.id}`}><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => role === 'admin' ? handleApprove(r.id, 'reject') : handleReview(r.id, 'reject')} data-testid={`reject-req-${r.id}`}><X size={14} /></Button>
                        </div>
                      )}
                      {r.status === 'reviewed' && role === 'admin' && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleApprove(r.id, 'approve')} data-testid={`final-approve-req-${r.id}`}><Check size={14} /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleApprove(r.id, 'reject')} data-testid={`final-reject-req-${r.id}`}><X size={14} /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nessuna richiesta</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── REWARDS TAB ───
function RewardsTab({ year, role, employees, employeeId }) {
  const [rewards, setRewards] = useState([]);
  const [budget, setBudget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ employee_id: '', reward_type: '', euro_amount: '' });

  const load = useCallback(async () => {
    try {
      const res = await api.get('/regards/rewards', { params: { year } });
      setRewards(res.data);
      if (employeeId) {
        const b = await api.get(`/regards/budget/${employeeId}`, { params: { year } });
        setBudget(b.data);
      }
    } catch {}
  }, [year, employeeId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      const empId = role === 'dipendente' ? employeeId : form.employee_id;
      await api.post('/regards/rewards', { employee_id: empId, reward_type: form.reward_type, euro_amount: parseFloat(form.euro_amount) });
      setShowAdd(false);
      setForm({ employee_id: '', reward_type: '', euro_amount: '' });
      load();
    } catch {}
  };

  const handleApprove = async (id, action) => {
    try {
      await api.put(`/regards/rewards/${id}/approve`, { action });
      load();
    } catch {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {budget && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Disponibile:</span>
            <span className="font-bold text-green-600">{budget.available_euro}€</span>
          </div>
        )}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild><Button size="sm" data-testid="redeem-reward-btn"><Gift size={14} className="mr-1" /> Riscatta Premio</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Riscatta Premio</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              {role !== 'dipendente' && (
                <Select value={form.employee_id} onValueChange={v => setForm(p => ({ ...p, employee_id: v }))}>
                  <SelectTrigger data-testid="reward-employee-select"><SelectValue placeholder="Dipendente" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Select value={form.reward_type} onValueChange={v => setForm(p => ({ ...p, reward_type: v }))}>
                <SelectTrigger data-testid="reward-type-select"><SelectValue placeholder="Tipo premio" /></SelectTrigger>
                <SelectContent>{REWARD_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" step="0.01" placeholder="Importo (€)" value={form.euro_amount} onChange={e => setForm(p => ({ ...p, euro_amount: e.target.value }))} data-testid="reward-amount-input" />
              <Button className="w-full" onClick={handleSubmit} disabled={!form.reward_type || !form.euro_amount || (role !== 'dipendente' && !form.employee_id)} data-testid="reward-submit-btn">Conferma Riscatto</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rewards-table">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2 px-2">Data</th>
                  <th className="py-2 px-2">Dipendente</th>
                  <th className="py-2 px-2">Tipo</th>
                  <th className="py-2 px-2 text-center">Importo</th>
                  <th className="py-2 px-2">Stato</th>
                  {role === 'admin' && <th className="py-2 px-2">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {rewards.map(r => (
                  <tr key={r.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-2 text-xs">{new Date(r.created_at).toLocaleDateString('it-IT')}</td>
                    <td className="py-2 px-2 font-medium">{r.employee_name}</td>
                    <td className="py-2 px-2">{REWARD_TYPES.find(rt => rt.value === r.reward_type)?.label || r.reward_type}</td>
                    <td className="py-2 px-2 text-center font-bold">{r.euro_amount}€</td>
                    <td className="py-2 px-2"><Badge className={`text-xs ${STATUS_COLORS[r.status]}`}>{STATUS_LABELS[r.status]}</Badge></td>
                    {role === 'admin' && (
                      <td className="py-2 px-2">
                        {r.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleApprove(r.id, 'approve')} data-testid={`approve-reward-${r.id}`}><Check size={14} /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleApprove(r.id, 'reject')} data-testid={`reject-reward-${r.id}`}><X size={14} /></Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {rewards.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nessun premio riscattato</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── CONFIG TAB (Admin only) ───
function ConfigTab({ year }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [bestWorkerMonth, setBestWorkerMonth] = useState(new Date().getMonth() + 1);
  const [bestWorker, setBestWorker] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/regards/config', { params: { year } });
      setConfig(res.data);
      setForm({ base_budget_euro: res.data.base_budget_euro, daily_request_limit: res.data.daily_request_limit });
    } catch {}
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const loadBestWorker = useCallback(async () => {
    try {
      const res = await api.get('/regards/best-worker', { params: { year, month: bestWorkerMonth } });
      setBestWorker(res.data);
    } catch {}
  }, [year, bestWorkerMonth]);

  useEffect(() => { loadBestWorker(); }, [loadBestWorker]);

  const handleSave = async () => {
    try {
      await api.put('/regards/config', { year, base_budget_euro: parseInt(form.base_budget_euro), daily_request_limit: parseInt(form.daily_request_limit) });
      load();
    } catch {}
  };

  const handleValidateBW = async () => {
    if (!bestWorker?.winners) return;
    try {
      await api.post('/regards/best-worker/validate', { year, month: bestWorkerMonth, winners: bestWorker.winners });
      loadBestWorker();
    } catch {}
  };

  if (!config) return null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Configurazione Sistema</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Budget base annuo (€)</label>
              <Input type="number" value={form.base_budget_euro || ''} onChange={e => setForm(p => ({ ...p, base_budget_euro: e.target.value }))} data-testid="config-budget-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Limite richieste giornaliere</label>
              <Input type="number" value={form.daily_request_limit || ''} onChange={e => setForm(p => ({ ...p, daily_request_limit: e.target.value }))} data-testid="config-limit-input" />
            </div>
          </div>
          <div className="text-xs text-slate-500 space-y-1">
            <p>Conversione: 100 punti = 10€ (1 punto = 0,10€)</p>
            <p>Premi Best Worker: 1° = 50€, 2° = 30€, 3° = 20€</p>
          </div>
          <Button onClick={handleSave} data-testid="config-save-btn">Salva Configurazione</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy size={18} className="text-yellow-500" /> Validazione Best Worker</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Select value={bestWorkerMonth.toString()} onValueChange={v => setBestWorkerMonth(parseInt(v))}>
            <SelectTrigger className="w-48" data-testid="bw-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>
                  {new Date(2026, i).toLocaleString('it-IT', { month: 'long' })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {bestWorker?.winners?.length > 0 ? (
            <div className="space-y-2">
              {bestWorker.winners.map(w => (
                <div key={w.employee_id} className="flex items-center gap-3 p-2 rounded border bg-slate-50">
                  <span className="font-bold text-lg">{w.position}°</span>
                  <span className="flex-1 font-medium">{w.employee_name}</span>
                  <span className="text-sm">{w.points} pt</span>
                  <span className="font-bold text-green-600">{w.prize_euro}€</span>
                </div>
              ))}
              {bestWorker.validated ? (
                <Badge className="bg-green-100 text-green-800">Validato</Badge>
              ) : (
                <Button onClick={handleValidateBW} data-testid="validate-bw-btn">Valida e Assegna Premi</Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Nessun dato per questo mese</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── PERSONAL DASHBOARD (dipendente view) ───
function PersonalDashboard({ year, employeeId }) {
  const [data, setData] = useState(null);
  const [budget, setBudget] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId) return;
    try {
      const [d, b] = await Promise.all([
        api.get('/regards/dashboard', { params: { year } }),
        api.get(`/regards/budget/${employeeId}`, { params: { year } }),
      ]);
      setData(d.data);
      setBudget(b.data);
    } catch {}
  }, [year, employeeId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !budget) return <div className="text-center py-8 text-slate-400">Caricamento...</div>;

  const budgetPct = budget.dynamic_limit > 0 ? Math.min(100, (budget.redeemed_euro / budget.dynamic_limit) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] font-semibold uppercase text-slate-400">Punti Totali</p>
            <p className="text-2xl font-black text-slate-900" data-testid="personal-total-points">{budget.total_points}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] font-semibold uppercase text-slate-400">Punti Mese</p>
            <p className="text-2xl font-black text-slate-900" data-testid="personal-month-points">{data.monthly_points}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] font-semibold uppercase text-slate-400">€ Disponibili</p>
            <p className="text-2xl font-black text-emerald-600" data-testid="personal-available-euro">{budget.available_euro}€</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] font-semibold uppercase text-slate-400">Accumulo Prossimo Anno</p>
            <p className="text-2xl font-black text-amber-600" data-testid="personal-excess-points">{budget.excess_points_next_year}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-500">Budget Annuo</span>
            <span className="font-medium">{budget.redeemed_euro}€ / {budget.dynamic_limit}€</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-testid="budget-bar">
            <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${budgetPct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>Base: {budget.base_budget}€</span>
            <span>Bonus: +{budget.bonus_added}€</span>
          </div>
        </CardContent>
      </Card>

      {data.category_breakdown && Object.keys(data.category_breakdown).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Ripartizione Categorie</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {CATEGORIES.map(cat => {
                const val = data.category_breakdown[cat.value] || 0;
                const maxVal = Math.max(1, ...Object.values(data.category_breakdown));
                const pct = (val / maxVal) * 100;
                return (
                  <div key={cat.value}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{cat.label}</span>
                      <span className="font-bold">{val} pt</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${cat.color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {data.badges && data.badges.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Award size={18} /> I Tuoi Badge</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.badges.map((b, i) => <BadgeIcon key={i} badge={b} />)}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── REGARDS EXPLANATION SECTION ───
function RegardsExplanation() {
  const [open, setOpen] = useState(false);

  return (
    <Card className="border-2 border-dashed border-blue-200 bg-gradient-to-br from-blue-50/80 via-white to-amber-50/60" data-testid="regards-explanation">
      <div
        className="flex items-center justify-between p-4 cursor-pointer select-none"
        onClick={() => setOpen(!open)}
        data-testid="regards-explanation-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <HelpCircle size={22} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-base">Come funziona il programma REGARDS?</h3>
            <p className="text-xs text-slate-500">Scopri come guadagnare punti e ottenere premi</p>
          </div>
        </div>
        {open ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
      </div>

      {open && (
        <CardContent className="pt-0 pb-5 px-5 space-y-5 animate-fade-in">
          {/* Intro */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-bold text-blue-700">REGARDS</span> e' il programma di gamification aziendale che premia il tuo impegno quotidiano.
              Ogni azione positiva ti fa guadagnare <span className="font-semibold text-green-600">punti</span> che puoi convertire in <span className="font-semibold text-amber-600">premi reali</span>!
            </p>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-bold text-sm text-slate-700 mb-3 flex items-center gap-2">
              <Star size={16} className="text-amber-500" /> Le 4 Categorie di Punti
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Zap size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-blue-800">Produzione</p>
                  <p className="text-xs text-blue-600">Qualita' del lavoro, rispetto tempi, efficienza sul cantiere</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 border border-purple-100">
                <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <Monitor size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-purple-800">Utilizzo Gestionale</p>
                  <p className="text-xs text-purple-600">Uso corretto dell'ERP, timbrature puntuali, compilazione documenti</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-100">
                <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Shield size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-green-800">Sicurezza</p>
                  <p className="text-xs text-green-600">Rispetto norme DPI, segnalazioni pericoli, comportamento sicuro</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <Award size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-amber-800">Comportamento</p>
                  <p className="text-xs text-amber-600">Collaborazione, puntualita', spirito di squadra, proattivita'</p>
                </div>
              </div>
            </div>
          </div>

          {/* Points System */}
          <div>
            <h4 className="font-bold text-sm text-slate-700 mb-3 flex items-center gap-2">
              <Flame size={16} className="text-orange-500" /> Come si guadagnano e perdono punti
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="p-3 rounded-lg bg-green-50 border border-green-100 text-center">
                <div className="text-2xl font-black text-green-600">+</div>
                <p className="font-semibold text-sm text-green-800">Punti Standard</p>
                <p className="text-xs text-green-600">Assegnati dal Capo Cantiere o QHSE per buone prestazioni quotidiane</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-center">
                <div className="text-2xl font-black text-blue-600">++</div>
                <p className="font-semibold text-sm text-blue-800">Bonus Speciale</p>
                <p className="text-xs text-blue-600">Assegnati dall'Admin per meriti straordinari o risultati eccezionali</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-center">
                <div className="text-2xl font-black text-red-600">-</div>
                <p className="font-semibold text-sm text-red-800">Malus Speciale</p>
                <p className="text-xs text-red-600">Sottratti dall'Admin per mancanze gravi, ritardi ripetuti o violazioni</p>
              </div>
            </div>
          </div>

          {/* Conversion & Rewards */}
          <div>
            <h4 className="font-bold text-sm text-slate-700 mb-3 flex items-center gap-2">
              <Gift size={16} className="text-pink-500" /> Conversione Punti e Premi
            </h4>
            <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Badge className="bg-blue-100 text-blue-800 text-xs">100 pt = 10 EUR</Badge>
                <span className="text-slate-600">Ogni punto vale <span className="font-bold">0,10 EUR</span></span>
              </div>
              <div className="text-sm text-slate-700">
                <p className="font-semibold mb-1">Premi disponibili:</p>
                <ul className="space-y-1 text-xs text-slate-600 ml-4">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Buono Carburante</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Buono Acquisto</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Buono Pasto</li>
                </ul>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
                Hai un budget annuo assegnato dall'azienda. I punti extra oltre il budget vengono accumulati per l'anno successivo!
              </div>
            </div>
          </div>

          {/* Best Worker */}
          <div>
            <h4 className="font-bold text-sm text-slate-700 mb-3 flex items-center gap-2">
              <Trophy size={16} className="text-yellow-500" /> Best Worker del Mese
            </h4>
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200">
              <p className="text-sm text-slate-700 mb-3">Ogni mese i 3 dipendenti con piu' punti vincono un premio extra:</p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-yellow-200">
                  <Crown size={18} className="text-yellow-500" />
                  <div>
                    <p className="text-xs font-bold text-yellow-700">1° Posto</p>
                    <p className="text-sm font-black text-slate-800">50 EUR</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200">
                  <Medal size={18} className="text-slate-400" />
                  <div>
                    <p className="text-xs font-bold text-slate-500">2° Posto</p>
                    <p className="text-sm font-black text-slate-800">30 EUR</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-amber-200">
                  <Award size={18} className="text-amber-700" />
                  <div>
                    <p className="text-xs font-bold text-amber-700">3° Posto</p>
                    <p className="text-sm font-black text-slate-800">20 EUR</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Badges */}
          <div>
            <h4 className="font-bold text-sm text-slate-700 mb-3 flex items-center gap-2">
              <Shield size={16} className="text-indigo-500" /> Badge e Riconoscimenti
            </h4>
            <p className="text-xs text-slate-600 mb-2">
              Raggiungi traguardi speciali per sbloccare badge oro, argento e bronzo nelle categorie:
              Produzione, Sicurezza, Innovazione, Gestionale e altro ancora!
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-yellow-100 border border-yellow-200">
                <Trophy size={13} className="text-yellow-500" />
                <span className="text-xs font-medium text-yellow-800">Oro</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-100 border border-slate-200">
                <Medal size={13} className="text-slate-400" />
                <span className="text-xs font-medium text-slate-600">Argento</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-amber-100 border border-amber-200">
                <Award size={13} className="text-amber-700" />
                <span className="text-xs font-medium text-amber-800">Bronzo</span>
              </div>
            </div>
          </div>

          {/* How to request */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h4 className="font-bold text-sm text-slate-700 mb-2 flex items-center gap-2">
              <Send size={14} className="text-blue-500" /> Come richiedere punti
            </h4>
            <ol className="text-xs text-slate-600 space-y-1.5 list-decimal list-inside">
              <li>Vai nella tab <span className="font-semibold">"Richieste"</span> e clicca "Nuova Richiesta"</li>
              <li>Seleziona il collega per cui vuoi richiedere i punti</li>
              <li>Scegli la categoria e descrivi la motivazione</li>
              <li>Il Capo Cantiere/QHSE revisiona la richiesta</li>
              <li>L'Admin approva definitivamente e i punti vengono assegnati</li>
            </ol>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─── MAIN PAGE ───
export default function RegardsPage() {
  const { user } = useAuth();
  const role = user?.role_name;
  const employeeId = user?.employee_id;
  const [employees, setEmployees] = useState([]);
  const [adminDash, setAdminDash] = useState(null);
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  useEffect(() => {
    api.get('/employees').then(res => {
      setEmployees(Array.isArray(res.data) ? res.data.filter(e => e.is_active) : []);
    }).catch(() => {});

    if (role !== 'dipendente') {
      api.get('/regards/dashboard', { params: { year } }).then(res => setAdminDash(res.data)).catch(() => {});
    }
  }, [role, year]);

  const isAdmin = role === 'admin';
  const canManage = ['admin', 'capo_cantiere', 'qhse'].includes(role);

  return (
    <div className="space-y-6" data-testid="regards-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="regards-title">REGARDS</h1>
          <p className="text-sm text-slate-500">Sistema di Gamification Aziendale</p>
        </div>
      </div>

      <RegardsExplanation />

      {canManage && adminDash && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] font-semibold uppercase text-slate-400">Dipendenti Attivi</p>
              <p className="text-2xl font-black" data-testid="kpi-employees">{adminDash.total_employees}</p>
            </CardContent>
          </Card>
          <Card className={adminDash.pending_approvals > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] font-semibold uppercase text-slate-400">Da Approvare</p>
              <p className="text-2xl font-black" data-testid="kpi-pending">{adminDash.pending_approvals}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] font-semibold uppercase text-slate-400">Punti Assegnati</p>
              <p className="text-2xl font-black" data-testid="kpi-total-points">{adminDash.total_points_assigned}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] font-semibold uppercase text-slate-400">Valore Totale €</p>
              <p className="text-2xl font-black" data-testid="kpi-total-euro">{adminDash.total_euro_value}€</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue={role === 'dipendente' ? 'dashboard' : 'classifica'} data-testid="regards-tabs">
        <TabsList className="flex-wrap h-auto gap-1">
          {role === 'dipendente' && <TabsTrigger value="dashboard" data-testid="tab-dashboard">La Mia Dashboard</TabsTrigger>}
          <TabsTrigger value="classifica" data-testid="tab-classifica">Classifica</TabsTrigger>
          {canManage && <TabsTrigger value="punti" data-testid="tab-punti">Punti</TabsTrigger>}
          <TabsTrigger value="richieste" data-testid="tab-richieste">Richieste</TabsTrigger>
          <TabsTrigger value="premi" data-testid="tab-premi">Premi</TabsTrigger>
          {isAdmin && <TabsTrigger value="config" data-testid="tab-config">Configurazione</TabsTrigger>}
        </TabsList>

        {role === 'dipendente' && (
          <TabsContent value="dashboard">
            <PersonalDashboard year={year} employeeId={employeeId} />
          </TabsContent>
        )}

        <TabsContent value="classifica">
          <LeaderboardTab year={year} month={month} />
        </TabsContent>

        {canManage && (
          <TabsContent value="punti">
            <PointsTab year={year} role={role} employees={employees} />
          </TabsContent>
        )}

        <TabsContent value="richieste">
          <RequestsTab year={year} role={role} employees={employees} />
        </TabsContent>

        <TabsContent value="premi">
          <RewardsTab year={year} role={role} employees={employees} employeeId={employeeId} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="config">
            <ConfigTab year={year} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
